import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import {
  Building2,
  ClipboardCheck,
  FolderKanban,
  Gauge,
  Handshake,
  HardHat,
  LocateFixed,
  MapPin,
  Megaphone,
  Receipt,
  Phone,
  Plug,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Users as UsersIcon,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ServiceTypes } from './settings/ServiceTypes';
import { Technicians } from './settings/Technicians';
import { LineItems } from './settings/LineItems';
import Metros from './settings/Metros';
import { CompanyProfile } from './settings/CompanyProfile';
import { Users } from './settings/Users';
import { Integrations } from './settings/Integrations';
import TechOverrides from './settings/TechOverrides';
import TechTracking from './settings/TechTracking';
import SuperAdmin from './settings/SuperAdmin';
import MarketingSources from './settings/MarketingSources';
import ProjectPipelines from './settings/ProjectPipelines';
import ImplementationPlans from './settings/ImplementationPlans';
import Usage from './settings/Usage';
import PhoneTracking from './settings/PhoneTracking';
import { ReviewRequests } from './settings/ReviewRequests';
import { ReferralPartners } from './settings/ReferralPartners';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { useVisibility } from '@/hooks/useVisibility';
import { useCompanySettings } from '@/hooks/useCompanySettings';

interface SettingsNavItem {
  value: string;
  label: string;
  icon: LucideIcon;
  content: React.ReactNode;
}

export default function Settings() {
  const { isSuperAdmin } = useSuperAdmin();
  const { canManageSettings } = useVisibility();
  const { enableExtendedProjects } = useCompanySettings();
  const [searchParams] = useSearchParams();
  // Returning from the Google Ads / Square / Xero OAuth flows lands here with
  // ?google_ads=..., ?square=... or ?xero=...; open the Integrations tab so
  // the user can see the result. ?tab=parts is the old name of Line Items.
  const requestedTab = searchParams.get('tab') === 'parts' ? 'line-items' : searchParams.get('tab');
  const initialTab = searchParams.has('google_ads') || searchParams.has('square') || searchParams.has('xero')
    ? 'integrations'
    : (requestedTab ?? 'service-types');
  const [activeTab, setActiveTab] = useState(initialTab);

  // Non-admins only get Technicians and Line Items; everything else is admin-only.
  // A ?tab= for anything not in this list falls back to the first item.
  const navItems: SettingsNavItem[] = canManageSettings
    ? [
        { value: 'company', label: 'Company Profile', icon: Building2, content: <CompanyProfile /> },
        { value: 'users', label: 'Users', icon: UsersIcon, content: <Users /> },
        { value: 'integrations', label: 'Integrations', icon: Plug, content: <Integrations /> },
        { value: 'phone', label: 'Phone & tracking', icon: Phone, content: <PhoneTracking /> },
        { value: 'usage', label: 'Call tracking usage', icon: Gauge, content: <Usage /> },
        { value: 'service-types', label: 'Service Types', icon: Wrench, content: <ServiceTypes /> },
        { value: 'techs', label: 'Technicians', icon: HardHat, content: <Technicians /> },
        { value: 'tech-tracking', label: 'Technician tracking', icon: LocateFixed, content: <TechTracking /> },
        { value: 'tech-overrides', label: 'Tech Overrides', icon: SlidersHorizontal, content: <TechOverrides /> },
        { value: 'line-items', label: 'Line Items', icon: Receipt, content: <LineItems /> },
        { value: 'marketing', label: 'Marketing', icon: Megaphone, content: <MarketingSources /> },
        { value: 'partners', label: 'Referral partners', icon: Handshake, content: <ReferralPartners /> },
        { value: 'reviews', label: 'Review requests', icon: Star, content: <ReviewRequests /> },
        { value: 'metros', label: 'Metros & Postcodes', icon: MapPin, content: <Metros /> },
        ...(enableExtendedProjects
          ? [{ value: 'projects', label: 'Projects', icon: FolderKanban, content: <ProjectPipelines /> }]
          : []),
        { value: 'implementation-plans', label: 'Data Integrity Plans', icon: ClipboardCheck, content: <ImplementationPlans /> },
        ...(isSuperAdmin
          ? [{ value: 'super-admin', label: 'Super Admin', icon: ShieldCheck, content: <SuperAdmin /> }]
          : []),
      ]
    : [
        { value: 'techs', label: 'Technicians', icon: HardHat, content: <Technicians /> },
        { value: 'line-items', label: 'Line Items', icon: Receipt, content: <LineItems /> },
      ];

  const activeItem = navItems.find((item) => item.value === activeTab) ?? navItems[0];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em]">Settings</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Manage your system configuration</p>
        </div>

        <div className="flex items-start gap-8">
          <nav aria-label="Settings sections" className="sticky top-0 w-[220px] shrink-0 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.value === activeItem.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setActiveTab(item.value)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-left text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="min-w-0 max-w-[820px] flex-1 space-y-4">{activeItem.content}</div>
        </div>
      </div>
    </Layout>
  );
}
